import { getConfig } from '../config';

interface ProductAttributeUpdate {
  productId: string;
  cpProductId: string;
  cpItemId: string;
  cpUrl: string;
  cpNameKr: string;
}

interface AttributePayload {
  products: Array<{
    productId: number;
    attributesToSet: Array<{
      key: string;
      value: string;
    }>;
  }>;
}

class ProductAttributesClient {
  /**
   * Update product attributes with Coupang information
   */
  public async updateProductAttributes(data: ProductAttributeUpdate): Promise<{ success: boolean; message: string }> {
    try {
      const apiUrl = this.getApiUrl();

      // Extract parent and child codes from the product/item IDs
      const cpParentCode = data.cpProductId;
      const cpChildCode = data.cpItemId;
      const cpProductCode = `${cpParentCode}-${cpChildCode}`;

      // Build the API payload
      const payload: AttributePayload = {
        products: [
          {
            productId: parseInt(data.productId),
            attributesToSet: [
              {
                key: 'cp_url',
                value: data.cpUrl,
              },
              {
                key: 'cp_product_code',
                value: cpProductCode,
              },
              {
                key: 'cp_parent_code',
                value: cpParentCode,
              },
              {
                key: 'cp_child_code',
                value: cpChildCode,
              },
              {
                key: 'cp_name_kr',
                value: data.cpNameKr,
              },
            ],
          },
        ],
      };

      console.info('📡 Updating product attributes:', {
        productId: data.productId,
        payload,
      });

      // Make API call to update product attributes
      const response = await fetch(`${apiUrl}/product/attributes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        console.info('✅ Successfully updated product attributes:', result);

        return {
          success: true,
          message: `Product attributes updated successfully! CP URL: ${data.cpUrl}`,
        };
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to update product attributes:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });

        return {
          success: false,
          message: `Failed to update product: ${response.status} ${response.statusText}`,
        };
      }
    } catch (error) {
      console.error('❌ Error updating product attributes:', error);
      return {
        success: false,
        message: `Error updating product: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  public async updateBarcode(
    productId: number,
    barcode: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const apiUrl = this.getApiUrl();

      const payload: AttributePayload = {
        products: [
          {
            productId,
            attributesToSet: [{ key: 'barcode', value: barcode }],
          },
        ],
      };

      const response = await fetch(`${apiUrl}/product/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        const productResult = result.results?.[0];
        if (productResult?.success === false) {
          const errorMsg = productResult.error || 'Unknown error';
          console.error('Barcode validation failed:', errorMsg);
          return { success: false, message: errorMsg };
        }
        return { success: true, message: `Barcode saved: ${barcode}` };
      } else {
        const errorText = await response.text();
        console.error('Failed to update barcode:', { status: response.status, error: errorText });
        return { success: false, message: `Failed to save barcode: ${response.status}` };
      }
    } catch (error) {
      console.error('Error updating barcode:', error);
      return { success: false, message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  }

  async copyUrlToClipboard(url: string): Promise<{ success: boolean; message: string }> {
    try {
      await navigator.clipboard.writeText(url);
      return {
        success: true,
        message: `URL copied to clipboard: ${url}`,
      };
    } catch (error) {
      console.error('❌ Failed to copy URL to clipboard:', error);
      return {
        success: false,
        message: 'Failed to copy URL to clipboard',
      };
    }
  }

  private getApiUrl(): string {
    const config = getConfig();
    return config.apiUrl;
  }
}

export const productAttributesClient = new ProductAttributesClient();
export type { ProductAttributeUpdate };
